import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { FileText, Eye, X } from "lucide-react";
import { useState, useRef, useCallback, useEffect } from "react";

interface Props {
  childId: string;
}

interface SchoolPlanImport {
  id: string;
  storage_path: string;
  week_number: number;
  created_at: string;
}

function isPdf(path: string) {
  return path.toLowerCase().endsWith(".pdf");
}

export function SchoolPlanViewer({ childId }: Props) {
  const [selectedPlan, setSelectedPlan] = useState<SchoolPlanImport | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);

  // zoom / pan state
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [zoomLabel, setZoomLabel] = useState<string | null>(null);
  const zoomTimer = useRef<ReturnType<typeof setTimeout>>();

  // touch refs
  const lastDist = useRef<number | null>(null);
  const lastCenter = useRef<{ x: number; y: number } | null>(null);
  const lastTap = useRef(0);
  const imgRef = useRef<HTMLDivElement>(null);

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ["school-plan-imports", childId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("school_plan_imports" as any)
        .select("id, storage_path, week_number, created_at")
        .eq("child_id", childId)
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return (data as any[]) as SchoolPlanImport[];
    },
    enabled: !!childId,
  });

  const resetZoom = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  const showZoomLabel = useCallback((s: number) => {
    setZoomLabel(`${Math.round(s * 100)}%`);
    clearTimeout(zoomTimer.current);
    zoomTimer.current = setTimeout(() => setZoomLabel(null), 800);
  }, []);

  async function openPlan(plan: SchoolPlanImport) {
    resetZoom();
    setSelectedPlan(plan);
    try {
      const { data, error } = await supabase.storage
        .from("school-plans")
        .createSignedUrl(plan.storage_path, 300);
      if (error) throw error;
      setSignedUrl(data.signedUrl);
    } catch {
      setSignedUrl(null);
    }
  }

  function closePlan() {
    setSelectedPlan(null);
    setSignedUrl(null);
    resetZoom();
  }

  // --- touch handlers ---
  const dist = (a: React.Touch, b: React.Touch) =>
    Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

  const center = (a: React.Touch, b: React.Touch) => ({
    x: (a.clientX + b.clientX) / 2,
    y: (a.clientY + b.clientY) / 2,
  });

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      lastDist.current = dist(e.touches[0], e.touches[1]);
      lastCenter.current = center(e.touches[0], e.touches[1]);
    } else if (e.touches.length === 1) {
      // double-tap detection
      const now = Date.now();
      if (now - lastTap.current < 300) {
        e.preventDefault();
        setScale((prev) => {
          const next = prev > 1.5 ? 1 : 2.5;
          if (next === 1) setTranslate({ x: 0, y: 0 });
          showZoomLabel(next);
          return next;
        });
      }
      lastTap.current = now;
      lastCenter.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  }, [showZoomLabel]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const d = dist(e.touches[0], e.touches[1]);
      const c = center(e.touches[0], e.touches[1]);
      if (lastDist.current != null) {
        const ratio = d / lastDist.current;
        setScale((prev) => {
          const next = Math.min(5, Math.max(1, prev * ratio));
          showZoomLabel(next);
          return next;
        });
      }
      if (lastCenter.current) {
        const dx = c.x - lastCenter.current.x;
        const dy = c.y - lastCenter.current.y;
        setTranslate((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      }
      lastDist.current = d;
      lastCenter.current = c;
    } else if (e.touches.length === 1 && scale > 1) {
      e.preventDefault();
      const t = e.touches[0];
      if (lastCenter.current) {
        const dx = t.clientX - lastCenter.current.x;
        const dy = t.clientY - lastCenter.current.y;
        setTranslate((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      }
      lastCenter.current = { x: t.clientX, y: t.clientY };
    }
  }, [scale, showZoomLabel]);

  const onTouchEnd = useCallback(() => {
    lastDist.current = null;
    lastCenter.current = null;
  }, []);

  // cleanup
  useEffect(() => () => clearTimeout(zoomTimer.current), []);

  if (isLoading || plans.length === 0) return null;

  const showPdf = selectedPlan && signedUrl && isPdf(selectedPlan.storage_path);

  return (
    <div className="space-y-2">
      <h2 className="text-lg font-display font-bold flex items-center gap-2">
        📄 Ukeplaner
      </h2>

      <div className="space-y-2">
        {plans.map((plan) => (
          <Card
            key={plan.id}
            className="cursor-pointer active:scale-[0.98] transition-all border-0 bg-card/80 shadow-sm"
            onClick={() => openPlan(plan)}
          >
            <CardContent className="p-3">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                  <FileText className="h-4.5 w-4.5 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">Uke {plan.week_number}</p>
                  <p className="text-[10px] text-muted-foreground">
                    Importert {new Date(plan.created_at).toLocaleDateString("nb-NO")}
                  </p>
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
                  <Eye className="h-3.5 w-3.5" />
                  Se
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!selectedPlan} onOpenChange={(o) => { if (!o) closePlan(); }}>
        <DialogContent
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-0 border-none max-w-none w-screen h-screen rounded-none [&>button]:hidden"
        >
          <DialogTitle className="sr-only">
            Ukeplan – Uke {selectedPlan?.week_number}
          </DialogTitle>

          {/* Close button */}
          <button
            onClick={closePlan}
            className="absolute top-4 right-4 z-[60] rounded-full bg-black/60 p-2 text-white hover:bg-black/80 transition-colors"
            aria-label="Lukk"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Zoom indicator */}
          {zoomLabel && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[60] bg-black/60 text-white text-xs font-medium px-3 py-1 rounded-full pointer-events-none">
              {zoomLabel}
            </div>
          )}

          {signedUrl ? (
            showPdf ? (
              <iframe
                src={signedUrl}
                className="w-full h-full border-none"
                title={`Ukeplan uke ${selectedPlan?.week_number}`}
              />
            ) : (
              <div
                ref={imgRef}
                className="w-full h-full overflow-hidden touch-none flex items-center justify-center"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                <img
                  src={signedUrl}
                  alt={`Ukeplan uke ${selectedPlan?.week_number}`}
                  className="max-w-full max-h-full object-contain select-none"
                  style={{
                    transform: `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`,
                    transformOrigin: "center center",
                    transition: lastDist.current != null ? "none" : "transform 0.15s ease-out",
                  }}
                  draggable={false}
                />
              </div>
            )
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              Kunne ikke laste ukeplanen. Den kan ha blitt slettet.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
